import UserRelationshipManager from "@src/components/Forms/Users/UserRelationshipManager";
import UserNotificationsForm from "@src/components/Forms/Users/UserNotificiationsForm";
import UserPreferencesForm from "@src/components/Forms/Users/UserPreferencesForm";
import SavingIndicator from "@src/components/SavingIndicator/SavingIndicator";
import { api } from "@src/utils/axios-instance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import UserInformation from "@src/components/Forms/Users/UserInformation";
import { Outlet } from "react-router";
import "./style/edit.sass";
import Page from "@src/layout/Page";
import Loader from "@src/components/Loader/Loader";
import {
    ColorScheme,
    NotificationType,
    Preferences,
    UserSettings,
} from "@src/types/global";

const UserEdit = () => {
    const fetchUserSettings = async (): Promise<UserSettings> => {
        const { data } = (await api.get("/users/settings")) as any;
        return data.data;
    };

    const fetchNotificationTypes = async (): Promise<NotificationType[]> => {
        const res = await api.get("/notification_types");
        return res.data.data;
    };
    const fetchColorSchemes = async (): Promise<ColorScheme[]> => {
        const res = await api.get("/color_schemes");
        return res.data.data;
    };
    const { data, isPending, isError, error, refetch } = useQuery<UserSettings>(
        {
            queryFn: fetchUserSettings,
            queryKey: ["userSettings"],
            staleTime: 60 * 1000,
        },
    );
    const queryClient = useQueryClient();
    const colorSchemeQuery = useQuery<ColorScheme[]>({
        queryFn: fetchColorSchemes,
        queryKey: ["colorSchemes"],
        staleTime: 60 * 1000,
        enabled: !!data,
    });
    const notificationTypeQuery = useQuery<NotificationType[]>({
        queryFn: fetchNotificationTypes,
        queryKey: ["notificationTypes"],
        staleTime: 60 * 1000,
        enabled: !!data?.notification_preferences,
    });
    const mutation = useMutation<Response, unknown, any>({
        mutationFn: (data: NotificationType[] | Preferences) => {
            return api.patch("/users", { params: data });
        },
        onSuccess: (_data) => {
            refetch();
            queryClient.setQueryData(["userPref"], data?.preferences);
        },
    });

    if (isPending) {
        return <Loader />;
    }

    if (isError) {
        return <span>Error: {error.message}</span>;
    }

    return (
        <Page>
            <SavingIndicator
                status={
                    (mutation.isPending && "SAVING") ||
                    (mutation.isSuccess && "SAVED")
                }
            />

            <div className="edit-page">
                <header>
                    <h1>Your Settings</h1>
                </header>
                <Outlet />
                <section>
                    <h2>General</h2>
                    <UserInformation />

                    <h2>Preferences</h2>
                    {colorSchemeQuery.data && (
                        <UserPreferencesForm
                            submitFormFn={(data) =>
                                mutation.mutate({ preferences: data })
                            }
                            userPreferences={data.preferences}
                            availableColorSchemes={colorSchemeQuery.data}
                        />
                    )}
                </section>
                <section>
                    <UserRelationshipManager />
                </section>
                <section>
                    <h2>Notifications</h2>
                    {notificationTypeQuery.data && (
                        <UserNotificationsForm
                            submitFormFn={(data) => mutation.mutate(data)}
                            userPreferences={data!.notification_preferences}
                            notificationTypes={notificationTypeQuery.data}
                        />
                    )}
                </section>
            </div>
        </Page>
    );
};
export default UserEdit;
